/**
 * financialCalculations.ts
 * Core financial engine: Age Pension, tax, deeming, cashflow, aged care costs.
 * Ported verbatim from frontend/src/calculations/FinancialCalculations.js — see the golden-master
 * regression suite (server/tests/rest/calculation/goldenMaster/) for the parity proof.
 *
 * `calculateFinancials` computes ONE year's assessment; {@link ../projectionEngine.ts} walks that
 * snapshot forward year by year, which is why the two now sit beside each other. This directory holds
 * the assessment that engine is built on — the means test, deeming, the pension, tax, expenses, net
 * worth, loan repayments, home equity release — with the individual rules injected as assessors owned
 * by the projection plugins (see `../plugins/`).
 *
 * It lived in `shared/` until the frontend stopped importing it. Nothing in `frontend/src` calls this
 * function: the browser reaches it over HTTP, through `/rest/calculation` (see calculationApi.ts).
 * What kept it in `shared/` was four small helpers that happened to share this file — they are now in
 * `shared/src/calculation/householdStatus.ts` and `giftingDeprivation.ts`.
 */

import { withExistingDepositRows } from '../../../../../shared/src/calculation/support/facilityMove.ts';
import { AGED_CARE_SCHEDULE } from '../../../../../shared/src/calculation/constants/agedCareSchedule.ts';
import { getGrannyFlatDeprivationFromFactFind, isGrannyFlatResidency } from '../../../../../shared/src/calculation/grannyFlatArrangement.ts';
import {
    PENSION_RATES,
    ASSET_TEST_LOWER_LIMITS,
    ASSET_TEST_UPPER_LIMITS,
    ASSET_TEST_LIMITS,
    INCOME_TEST_LOWER_LIMITS,
    INCOME_TEST_CUTOFF_LIMITS,
    DEEMING_THRESHOLDS,
    TAX_BRACKETS,
    MEDICARE_LEVY_THRESHOLDS,
    EXTRA_ALLOWABLE_AMOUNT, taxBracketsForFinancialYear, MEDICARE_LEVY_SURCHARGE } from '../../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { calculateAge } from '../../../../../shared/src/calculation/support/dateHelpers.ts';
import { formatCurrency } from '../../../../../shared/src/calculation/support/currency.ts';
import { calculateAgePension, type AgePensionAssessor } from './agePension.ts';
import { calculateAssessableMeans, type AssessableMeansAssessor } from './assessableMeans.ts';
import { calculateDeemedIncome, type DeemedIncomeAssessor } from './deemedIncome.ts';
import { calculateExpenses, type ExpenseAssessor } from './expenses.ts';
import { calculateNetWorthPosition, type NetWorthAssessor } from './netWorth.ts';
import { calculateHomeEquityRelease, type HomeEquityReleaseAssessor } from './homeEquityRelease.ts';
import { calculateIncomeAssessment, type IncomeAssessor } from '../../../../../shared/src/calculation/incomeAssessment.ts';
import { calculateIncomeTax, buildBaseTaxOffsets, scaleTaxOffsets, TAX_BRACKETS_WITH_PREV, type IncomeTaxAssessor, withPreviousLimits } from '../../../../../shared/src/calculation/incomeTax.ts';
import { calculateLoanRepayments, type LoanRepaymentAssessor } from './loanRepayments.ts';
import { computeStructureAttribution } from '../../../../../shared/src/calculation/structureAttribution.ts';
import { getEntityHomeAssessableForAgedCare, isEntityHeldStep3Home } from '../../../../../shared/src/calculation/entityHomeRules.ts';
import { applyRadFundingCgt } from '../../../../../shared/src/calculation/entityDisposalTax.ts';
import type { FactFind, FactFindExpenses, FactFindPerson } from '../../../../../shared/src/calculation/factFindTypes.ts';
import type { StrategyOptions } from '../../../../../shared/src/calculation/strategyOptionsTypes.ts';
import { isIllnessSeparatedCouple } from '../../../../../shared/src/calculation/householdStatus.ts';
import { assessPensionHomeowner } from '../../../../../shared/src/calculation/householdPosition.ts';

/**
 * Build indexed rates for the `yearIndex`th year of a projection — the ORDINAL (1, 2, 3 …), not a
 * calendar or financial year. Year 1 = base; year 2+ = base × (1+r)^(yearIndex-1), so the first year
 * uses current rates and future years use assumed indexation.
 *
 * Every legislated figure is indexed this way: tax brackets, pension rates, asset-test limits and the
 * Medicare thresholds all escalate off one base set by CPI. Nothing here is keyed to a named financial
 * year, which is why the engine is free to cut its rows on either calendar (see
 * plugins/engineTypes.ts).
 */
/*
 * The rates for a (year, CPI assumption, financial year) are fixed, and a projection asks for them on every
 * assessment — an opening one each year and a pension re-assessment every fortnight, for every candidate —
 * so they are worked out once per key and reused (24 Oct 2026: a cold aged care analysis ran past its time
 * limit). Nothing reads them but to read: no caller mutates a rates object (the base year already handed
 * out the shared constant tables).
 */
const indexedRatesCache = new Map<string, ReturnType<typeof getIndexedRatesUncached>>();
function getIndexedRates(yearIndex: number, inflationPct?: number | null, taxYearStart?: number | null) {
    const key = `${yearIndex}|${inflationPct}|${taxYearStart}`;
    let rates = indexedRatesCache.get(key);
    if (!rates) {
        rates = getIndexedRatesUncached(yearIndex, inflationPct, taxYearStart);
        if (indexedRatesCache.size > 512) indexedRatesCache.clear();
        indexedRatesCache.set(key, rates);
    }
    return rates;
}

function getIndexedRatesUncached(yearIndex: number, inflationPct?: number | null, taxYearStart?: number | null) {
    // THE ONE FY-AWARE TABLE. Every other figure indexes off the run's first year by CPI; the marginal
    // brackets are the one table that changes by legislation on a named date (14% from 1 July 2027),
    // so they are chosen by the financial year the projection year opens in, then indexed.
    const baseBrackets = taxBracketsForFinancialYear(taxYearStart);
    if (yearIndex <= 1 || inflationPct == null) {
        return {
            pensionRates: PENSION_RATES,
            assetTestLower: ASSET_TEST_LOWER_LIMITS,
            assetTestUpper: ASSET_TEST_UPPER_LIMITS,
            assetTestLimits: ASSET_TEST_LIMITS,
            incomeTestLower: INCOME_TEST_LOWER_LIMITS,
            incomeTestCutoff: INCOME_TEST_CUTOFF_LIMITS,
            deemingThresholds: DEEMING_THRESHOLDS,
            taxBrackets: withPreviousLimits(baseBrackets),
            medicareLevyThresholds: MEDICARE_LEVY_THRESHOLDS,
            medicareLevySurcharge: MEDICARE_LEVY_SURCHARGE,
            taxOffsets: buildBaseTaxOffsets(),
        };
    }
    const f = Math.pow(1 + inflationPct / 100, yearIndex - 1);
    const scale = (x: number) => Math.round(x * f);
    const scaleObj = (o: { single: number; couple: number }) => ({ single: scale(o.single), couple: scale(o.couple) });
    const scaleAssetLimitTree = (tree: typeof ASSET_TEST_LIMITS) => ({
        single: {
            homeowner: { full: scale(tree.single.homeowner.full), cutoff: scale(tree.single.homeowner.cutoff) },
            nonHomeowner: { full: scale(tree.single.nonHomeowner.full), cutoff: scale(tree.single.nonHomeowner.cutoff) },
        },
        couple: {
            homeowner: { full: scale(tree.couple.homeowner.full), cutoff: scale(tree.couple.homeowner.cutoff) },
            nonHomeowner: { full: scale(tree.couple.nonHomeowner.full), cutoff: scale(tree.couple.nonHomeowner.cutoff) },
        },
        coupleIllnessSeparated: {
            homeowner: { full: scale(tree.coupleIllnessSeparated.homeowner.full), cutoff: scale(tree.coupleIllnessSeparated.homeowner.cutoff) },
            nonHomeowner: { full: scale(tree.coupleIllnessSeparated.nonHomeowner.full), cutoff: scale(tree.coupleIllnessSeparated.nonHomeowner.cutoff) },
        },
    });
    const taxBrackets = baseBrackets.map((b, i) => ({
        limit: b.limit === Infinity ? Infinity : scale(b.limit),
        rate: b.rate,
        base: scale(b.base),
        previousLimit: i === 0 ? 0 : scale(baseBrackets[i - 1]!.limit),
    }));
    const medicareLevyThresholds = {
        single: { lower: scale(MEDICARE_LEVY_THRESHOLDS.single.lower), upper: scale(MEDICARE_LEVY_THRESHOLDS.single.upper) },
        couple: { lower: scale(MEDICARE_LEVY_THRESHOLDS.couple.lower), upper: scale(MEDICARE_LEVY_THRESHOLDS.couple.upper) },
        singleSenior: { lower: scale(MEDICARE_LEVY_THRESHOLDS.singleSenior.lower), upper: scale(MEDICARE_LEVY_THRESHOLDS.singleSenior.upper) },
        coupleSenior: { lower: scale(MEDICARE_LEVY_THRESHOLDS.coupleSenior.lower), upper: scale(MEDICARE_LEVY_THRESHOLDS.coupleSenior.upper) },
        perDependentChild: scale(MEDICARE_LEVY_THRESHOLDS.perDependentChild),
        perDependentChildUpper: scale(MEDICARE_LEVY_THRESHOLDS.perDependentChildUpper),
    };
    // The surcharge tiers index each 1 July (AWOTE in law; CPI here, like every other figure).
    const medicareLevySurcharge = {
        tiers: MEDICARE_LEVY_SURCHARGE.tiers.map((t) => ({
            singleUpTo: t.singleUpTo === Infinity ? Infinity : scale(t.singleUpTo),
            familyUpTo: t.familyUpTo === Infinity ? Infinity : scale(t.familyUpTo),
            rate: t.rate,
        })),
        perExtraChild: scale(MEDICARE_LEVY_SURCHARGE.perExtraChild),
    };
    return {
        pensionRates: scaleObj(PENSION_RATES),
        assetTestLower: scaleObj(ASSET_TEST_LOWER_LIMITS),
        assetTestUpper: scaleObj(ASSET_TEST_UPPER_LIMITS),
        assetTestLimits: scaleAssetLimitTree(ASSET_TEST_LIMITS),
        incomeTestLower: scaleObj(INCOME_TEST_LOWER_LIMITS),
        incomeTestCutoff: {
            single: scale(INCOME_TEST_CUTOFF_LIMITS.single),
            couple: scale(INCOME_TEST_CUTOFF_LIMITS.couple),
            coupleIllnessSeparated: scale(INCOME_TEST_CUTOFF_LIMITS.coupleIllnessSeparated),
        },
        deemingThresholds: scaleObj(DEEMING_THRESHOLDS),
        taxBrackets,
        medicareLevyThresholds,
        medicareLevySurcharge,
        taxOffsets: scaleTaxOffsets(scale),
    };
}

/** Overridable pieces of the core engine. See {@link CalculateFinancialsOptions.assessAgePension}. */
export interface CalculateFinancialsOptions {
    /**
     * How this call decides what counts as an assessable asset and as assessable income — the totals
     * both means tests are struck on. Defaults to {@link calculateAssessableMeans}.
     *
     * A seam for the same reason as {@link assessAgePension}: the assessment feeds this function's own
     * pension calculation (and so its `cashFlow`), and the totals are reported back out for the
     * aged-care means test, so the projection engine's assessableMeansPlugin cannot apply rules of its
     * own after the fact. It supplies the assessor instead.
     */
    assessAssessableMeans?: AssessableMeansAssessor;
    /**
     * How this call deems the client's financial assets, and how it treats what they have given away.
     * Defaults to {@link calculateDeemedIncome}.
     *
     * A seam for the same reason as {@link assessAssessableMeans}, which it takes its base from: deemed
     * income is the largest component of most retirees' assessable income, so it decides the pension
     * this function folds into its `cashFlow`. The projection engine's deemedIncomePlugin supplies the
     * assessor.
     */
    assessDeemedIncome?: DeemedIncomeAssessor;
    /**
     * How this call assesses a Reverse Mortgage or Home Equity Access Scheme strategy. Defaults to
     * {@link calculateHomeEquityRelease}.
     *
     * A seam for the same reason as the others: the payments are cash income inside this function's
     * `cashFlow` and `cashFlowBreakdown`, and any assessable lump-sum proceeds change the means-test
     * position the pension is struck on, so the projection engine's loanPlugin — which compounds the
     * balances these payments accrue against — cannot derive them after the fact. It supplies the
     * assessor instead, which is what puts both halves of these loans under that one plugin.
     */
    assessHomeEquityRelease?: HomeEquityReleaseAssessor;
    /**
     * How this call assesses what the client's assets and income streams earn — the tax position and the
     * cash position both. Defaults to {@link calculateIncomeAssessment}.
     *
     * A seam for the same reason as the others, and the broadest of them: the assessment IS this
     * function's taxable income and most of its `cashFlow`, so the projection engine's incomePlugin —
     * which pays the salary and rent components out monthly — cannot derive them after the fact. It
     * supplies the assessor instead, which puts both what the income is and when it arrives under that
     * one plugin.
     */
    assessIncome?: IncomeAssessor;
    /**
     * How this call taxes the two assessed positions. Defaults to {@link calculateIncomeTax}.
     *
     * A seam for the same reason as the others: the tax is an expense inside this function's `cashFlow`
     * and its `cashFlowBreakdown`, so the projection engine's incomeTaxPlugin cannot apply rules of its
     * own after the fact. It supplies the assessor instead.
     */
    assessIncomeTax?: IncomeTaxAssessor;
    /**
     * How this call assesses the year's living, capital, partner-rent and aged-care costs. Defaults to
     * {@link calculateExpenses}.
     *
     * A seam for the same reason as the others: the expenses are subtracted inside this function's
     * `cashFlow`, and `livingExpensesCash` is reported back out for the projection engine's expensesPlugin
     * to charge fortnightly, so that plugin cannot re-derive them after the fact.
     */
    assessExpenses?: ExpenseAssessor;
    /**
     * How this call totals the balance sheet. Defaults to {@link calculateNetWorthPosition}.
     *
     * The one seam that is about ownership alone rather than necessity — `netWorth` is a reported figure
     * that nothing else here consumes, so a caller could in principle recompute it afterwards. It is
     * injected anyway so that the projection engine's netWorthPlugin owns the rule in the same way every
     * other plugin owns its own, rather than this being the single exception.
     */
    assessNetWorth?: NetWorthAssessor;
    /**
     * How this call assesses the Age Pension entitlement. Defaults to {@link calculateAgePension} —
     * the ordinary means test — which is what every standalone (single-year) caller wants.
     *
     * It is a seam rather than a constant because the multi-year projection owns pension rules of its
     * own: the projection engine passes the assessor exported by its `agePensionPlugin`, which is
     * where both the entitlement and the fortnightly payment of it are described. The pension is part
     * of this function's `cashFlow`, so it cannot simply be computed after the fact by such a caller.
     */
    assessAgePension?: AgePensionAssessor;
    /**
     * How this call assesses the client's loan repayments and the interest deductions they generate.
     * Defaults to {@link calculateLoanRepayments}.
     *
     * A seam for the same reason as {@link assessAgePension}: the repayments are part of this
     * function's `cashFlow` and the deductions part of its tax calculation, so the projection engine's
     * loanRepaymentPlugin — which pays each liability at its own weekly/fortnightly/monthly cadence —
     * cannot compute them after the fact. It supplies the assessor instead.
     */
    assessLoanRepayments?: LoanRepaymentAssessor;
}

/**
 * Where a caller wants the Age Pension and nothing after it (see {@link calculateAgePensionEntitlement}):
 * the assessment stops once section 3 has struck the entitlement and writes it here.
 */
interface AgePensionProbe {
    agePension: number;
}

// projectionContext: { yearIndex, inflationPct, projectionAnchorDate? } for multi-year projections.
// Year 1 uses current rates; Year 2+ use indexed rates.
const calculateFinancialsCore = (
    factFind: FactFind,
    strategyOptions: StrategyOptions = {},
    yearsSinceRented = 0,
    projectionContext: any = {},
    {
        assessAssessableMeans = calculateAssessableMeans,
        assessDeemedIncome = calculateDeemedIncome,
        assessHomeEquityRelease = calculateHomeEquityRelease,
        assessIncome = calculateIncomeAssessment,
        assessIncomeTax = calculateIncomeTax,
        assessExpenses = calculateExpenses,
        assessNetWorth = calculateNetWorthPosition,
        assessAgePension = calculateAgePension,
        assessLoanRepayments = calculateLoanRepayments,
    }: CalculateFinancialsOptions = {},
    pensionProbe?: AgePensionProbe,
) => {
    // Someone in care now brings their deposit with them: exempt for the pension, counted for the care
    // means test (support/facilityMove.ts, 8 Oct 2026). Unchanged where the profile records none.
    factFind = withExistingDepositRows(factFind);
    const yearIndex = projectionContext?.yearIndex;
    const inflationPct = projectionContext?.inflationPct;
    // The financial year this assessment is struck in (by its opening calendar year) — the projection
    // supplies it; a single-year caller means "now", the year the unqualified tables describe.
    const taxYearStart: number | undefined = projectionContext?.taxYearStart;
    const useIndexedRates = yearIndex != null && yearIndex > 1 && inflationPct != null;
    const rates = useIndexedRates ? getIndexedRates(yearIndex, inflationPct, taxYearStart) : getIndexedRates(1, null, taxYearStart);

    const { client = {}, partner = {}, adviceFor, expenses = {} } = factFind;
    const isPartnered = adviceFor === 'couple';
    // `__age` when the caller is a PROJECTION — birthdayPlugin seeds it from the run's anchor and
    // advances it on each person's real birthday, and this assessment is struck on 1 July, so it is
    // the age they held when the financial year opened. Falling back to the date of birth is for the
    // single-year callers (the /rest/calculation endpoints, the strategy previews), which have no run
    // to be partway through and mean "as at today".
    //
    // Reading it matters more here than anywhere else the engine asks an age. This function decides
    // Age Pension age, the SAPTO thresholds and the aged-care means test, and it used to derive both
    // ages from `dob` against the wall clock — so a projection assessed year 10 with the client's
    // age TODAY, and someone who reached pension age in year 4 never reached it at all.
    const ageOf = (person: FactFindPerson) => person?.__age ?? (person?.dob ? calculateAge(person.dob) : null);
    const clientAge = ageOf(client);
    const partnerAge = isPartnered ? ageOf(partner) : null;
    const relationshipStatus = isPartnered ? 'couple' : 'single';
    const illnessSeparated = isIllnessSeparatedCouple(factFind, strategyOptions);
    const clientResidency = strategyOptions?.clientResidencyOverride || factFind?.client?.currentResidency;
    const partnerResidency = strategyOptions?.partnerResidencyOverride || factFind?.partner?.currentResidency;
    /*
     * Homeowner status, the village and granny flat tests — one rule for every assessor and screen
     * (shared/src/calculation/householdPosition.ts, 7 Oct 2026). The order and the former-home
     * retention it applies are documented there; `someoneInCare` keeps the name the rest of this
     * function reads.
     */
    const homeowner = assessPensionHomeowner(factFind, strategyOptions);
    const someoneInCare = clientResidency === 'aged_care' || (isPartnered && partnerResidency === 'aged_care');
    const { isHomeowner, rvResident, rvInterestHeld, rvIsHomeowner, rvEntryContribution, grannyFlatIsHomeowner, grannyFlatResident, grannyFlatEntryContribution } = homeowner;
    // A granny flat interest at or below the extra allowable amount: the resident is a non-homeowner
    // and what they paid is a counted asset, the same "special residence" rule as a village entry
    // contribution (owner-caught, 7 Oct 2026: only the village side was counted).
    // What is counted is the value of the right — what was given less any part that was a gift. The gift is
    // counted once, as a deprived asset (deemedIncome.ts); counting the whole amount here as well counted it
    // twice (owner's granny flat review, 24 Oct 2026; pack v1.58 — Social Security Guide 4.6.4.30).
    const grannyFlatGift = grannyFlatResident && !grannyFlatIsHomeowner
        ? (getGrannyFlatDeprivationFromFactFind(factFind, rates.pensionRates.couple)?.deprivation ?? 0)
        : 0;
    const grannyFlatCountedInterest = grannyFlatResident && !grannyFlatIsHomeowner ? Math.max(0, grannyFlatEntryContribution - grannyFlatGift) : 0;

    // --- 1. Assessable Assets & Income (for Pension) ---
    // What COUNTS as an assessable asset and as assessable income lives in assessableMeans.ts and is
    // INJECTED (`options.assessAssessableMeans`, defaulting to `calculateAssessableMeans`) so a caller
    // can own the rules — the projection engine passes in the assessor owned by its
    // assessableMeansPlugin. These totals are struck once and read TWICE: by the Age Pension means test
    // in section 3, and by the aged-care means test, which reads them back off this function's result.
    //
    // `structureAttribution` is resolved first because the walk merges the trust/company apportionment
    // into its totals. It stays here rather than moving into the assessor: the same apportionment also
    // carries the entity tax lines and the pooled entity loan repayments, which sections 4 and 5 read.
    const structureAttribution = computeStructureAttribution(factFind, { isPartnered, clientAge, partnerAge, strategyOptions });
    const means = assessAssessableMeans({
        factFind,
        strategyOptions,
        isPartnered,
        clientAge,
        partnerAge,
        clientResidency,
        partnerResidency,
        yearsSinceRented,
        rvResident,
        rvInterestHeld,
        grannyFlatCountedInterest,
        rvIsHomeowner,
        rvEntryContribution,
        structureAttribution,
    });
    const {
        assessableAssetsNoHome,
        assessableIncomeFromPensions,
        netRentalIncomeForPension,
        otherAssessableIncome,
        investmentPropertyRentalForPension,
        partnerAssessableIncomeFromPensions,
        partnerNetRentalIncomeForPension,
        partnerOtherAssessableIncome,
        partnerInvestmentPropertyRentalForPension,
        pre2015ABPDetails,
        definedBenefitDetails,
    } = means;
    // These two are still added to below — by deprivation (section 2) and, where the client takes a
    // reverse mortgage they don't spend on a RAD, by the loan proceeds (section 3).
    let { assessableAssets, financialAssetsForDeeming } = means;

    // --- 2. Deemed Income from Financial Assets ---
    // Deeming and deprivation live in deemedIncome.ts and are INJECTED (`options.assessDeemedIncome`,
    // defaulting to `calculateDeemedIncome`) so a caller can own the rules — the projection engine
    // passes in the assessor owned by its deemedIncomePlugin. The two are assessed together because a
    // deprived asset is deemed as well as counted, so the assessment hands back both the income and the
    // enlarged totals it was struck on.
    const projectionAnchorDate =
        projectionContext?.projectionAnchorDate != null
            ? projectionContext.projectionAnchorDate instanceof Date
                ? projectionContext.projectionAnchorDate
                : new Date(projectionContext.projectionAnchorDate)
            : new Date();

    // A projected year is assessed as at its own opening day (`assessmentDate`), so a gift ages out
    // of deprivation five years on; a standalone assessment, and a run's first year, use the anchor.
    const assessmentDate: Date =
        projectionContext?.assessmentDate instanceof Date
            ? projectionContext.assessmentDate
            : projectionContext?.assessmentDate != null
                ? new Date(projectionContext.assessmentDate)
                : projectionAnchorDate;
    const deeming = assessDeemedIncome({ rates, relationshipStatus, factFind, strategyOptions, asOfDate: assessmentDate, assessableAssets, financialAssetsForDeeming });
    const { deemedIncome, deemingThreshold, deprivedAsset } = deeming;
    assessableAssets = deeming.assessableAssets;
    financialAssetsForDeeming = deeming.financialAssetsForDeeming;

    // --- 3. Reverse Mortgage & HEAS Calculations ---
    // The two home-equity-release strategies live in homeEquityRelease.ts and are INJECTED
    // (`options.assessHomeEquityRelease`, defaulting to `calculateHomeEquityRelease`) so a caller can own
    // the rules — the projection engine passes in the assessor owned by its loanPlugin, which is already
    // the plugin that compounds these two loans' balances. Both the income assessed here and the balance
    // that income accrues against are therefore described in one place.
    //
    // Note the deeming above has already been struck: lump-sum proceeds enlarge the totals but are not
    // deemed in the year the loan is drawn. Long-standing behaviour, pinned by the golden master.
    const homeEquityRelease = assessHomeEquityRelease({ strategyOptions, factFind, assessableAssets, financialAssetsForDeeming });
    const { reverseMortgageIncome, heasIncome, heasAgePensionReduction } = homeEquityRelease;
    assessableAssets = homeEquityRelease.assessableAssets;
    financialAssetsForDeeming = homeEquityRelease.financialAssetsForDeeming;

    // --- 3. Age Pension Calculation ---
    // The means test itself lives in agePension.ts and is INJECTED (`options.assessAgePension`,
    // defaulting to `calculateAgePension`) so a caller can own the rules — the projection engine
    // passes in the assessor owned by its agePensionPlugin, which also pays the result out
    // fortnightly. Everything above is the shared assessment of assets and income that both this and
    // the aged-care means test are struck on; everything below consumes the entitlement.
    const { agePension, rentAssistanceIncome, totalAssessableIncome, socialSecurityBreakdown } = assessAgePension({
        rates,
        isPartnered,
        relationshipStatus,
        illnessSeparated,
        isHomeowner,
        clientAge,
        partnerAge,
        assessableAssets,
        deprivedAsset,
        formerHomeCounted: strategyOptions.formerHomeExemptionEnded ? Number(factFind.home?.value) || 0 : 0,
        deemedIncome,
        deemingThreshold,
        financialAssetsForDeeming,
        assessableIncomeFromPensions,
        netRentalIncomeForPension,
        otherAssessableIncome,
        investmentPropertyRentalForPension,
        partnerAssessableIncomeFromPensions,
        partnerNetRentalIncomeForPension,
        partnerOtherAssessableIncome,
        partnerInvestmentPropertyRentalForPension,
        clientResidency,
        partnerResidency,
        rvResident,
        rvIsHomeowner,
        factFind,
        heasAgePensionReduction,
        structureAttribution,
        pre2015ABPDetails,
        definedBenefitDetails,
        // The pension alone needs no working shown (see calculateAgePensionEntitlement).
        ...(pensionProbe ? { withoutBreakdown: true } : {}),
    });
    // Asked for the pension alone: everything below only consumes it (income walk, loans, tax, the
    // cashflow and its breakdown), so stopping here cannot change the figure — see calculateAgePensionEntitlement.
    if (pensionProbe) {
        pensionProbe.agePension = agePension;
        return null;
    }

    // --- 4. Taxable Income & Cash Income Calculation ---
    // The income walk lives in incomeAssessment.ts and is INJECTED (`options.assessIncome`, defaulting
    // to `calculateIncomeAssessment`) so a caller can own the rules — the projection engine passes in
    // the assessor owned by its incomePlugin, which then pays the salary and rent components monthly
    // instead of settling the lot on 30 June. Both the TAX position and the CASH buckets come back: they
    // are not the same figure (a reinvested dividend is taxed but never banked, an ABP drawdown from age
    // 60 is banked but never taxed), and section 6 below needs the one while section 7 needs the other.
    //
    // `clientTaxable`/`partnerTaxable` come back as live objects because section 5 appends this year's
    // loan-interest deductions to them before the tax is struck.
    const incomeAssessment = assessIncome({ factFind, strategyOptions, isPartnered, clientAge, partnerAge, structureAttribution });
    const {
        clientTaxable,
        partnerTaxable,
        investmentIncomeCash,
        investmentIncomeParts,
        abpIncomeCash,
        otherIncomeCash,
        salaryIncomeCash,
        reinvestedDividends,
        investmentPropertyRentCash,
        investmentPropertyExpenseCash,
    } = incomeAssessment;
    // Section 5's entity loan pool is added to this below.
    let totalRentalExpenses = incomeAssessment.totalRentalExpenses;

    // Always empty today (see entityDisposalTax.ts) — the seam for entity CGT triggered by RAD funding,
    // consumed by the cashflow breakdown in section 7.
    const { extraCashflowItems: radFundingEntityCgtItems } = applyRadFundingCgt();

    // --- 5. Loan Repayments & Deductions ---
    // The liabilities walk lives in loanRepayments.ts and is INJECTED (`options.assessLoanRepayments`,
    // defaulting to `calculateLoanRepayments`) so a caller can own the rules — the projection engine
    // passes in the assessor owned by its loanRepaymentPlugin, which then pays each schedule out at its
    // own frequency instead of settling the lot on 30 June. The DEDUCTIONS have to come back here
    // rather than stay with that plugin: they are part of this function's tax calculation below.
    // `yearsElapsed` takes a year off every loan's remaining term per projected year, so an
    // auto-calculated instalment stays the one the client really pays and the loan ends on time.
    // Year 1 (and any standalone call, where `yearIndex` is absent) is the present: 0 elapsed.
    const loanRepayments = assessLoanRepayments({ factFind, isPartnered, yearsElapsed: yearIndex != null && yearIndex > 1 ? yearIndex - 1 : 0 });
    let totalLoanRepayments = loanRepayments.totalAnnualRepayments;
    for (const deduction of loanRepayments.deductions) {
        (deduction.owner === 'client' ? clientTaxable : partnerTaxable).deductionSources.push({ name: deduction.name, amount: deduction.amount });
    }

    // Trust/company liabilities are excluded from the walk above and pooled here instead, already
    // annualised. They stay a single annual figure: a pool has no one servicing frequency, so unlike
    // the personal schedules there is nothing for the projection engine to time.
    if (structureAttribution?.loanRepaymentsAnnual) {
        totalLoanRepayments += structureAttribution.loanRepaymentsAnnual;
    }

    // --- 6. Tax Calculation ---
    // The tax system lives in incomeTax.ts and is INJECTED (`options.assessIncomeTax`, defaulting to
    // `calculateIncomeTax`) so a caller can own the rules — the projection engine passes in the assessor
    // owned by its incomeTaxPlugin. It runs HERE, after section 5, because the loan-interest deductions
    // appended up there are part of what is taxed.
    //
    // Both people are taxed in one call: a couple's Medicare levy thresholds are struck on their COMBINED
    // income, so neither can be assessed without the other.
    const incomeTax = assessIncomeTax({
        rates,
        clientTaxable,
        partnerTaxable,
        isPartnered,
        illnessSeparated,
        relationshipStatus,
        client,
        partner,
        clientAge,
        partnerAge,
    });
    const { totalTax, taxBreakdown } = incomeTax;
    const clientTotalTaxable = incomeTax.client.totalTaxable;
    const partnerTotalTaxable = incomeTax.partner.totalTaxable;

    // --- 7. Final Cash Flow & Net Worth ---
    // Two of the three things this section does are assessments with rules of their own, and both are
    // INJECTED so a caller can own them: the year's expenses (`options.assessExpenses`, defaulting to
    // `calculateExpenses` — the projection engine passes in the assessor owned by its expensesPlugin,
    // which then charges the living and rental components fortnightly) and the balance sheet
    // (`options.assessNetWorth`, defaulting to `calculateNetWorthPosition`, owned by its netWorthPlugin).
    //
    // The third — striking the cashflow and rendering it line by line — stays here. It is not a rule but
    // this function's summing-up: every figure below has already been assessed by one of the seven
    // assessors above, and adding them together is `calculateFinancials`'s own contract rather than any
    // plugin's concern. The projection engine draws the same line, keeping its `engine.cashflowCalc`
    // phase native rather than giving it to a plugin.
    const { netWorth } = assessNetWorth({ factFind, strategyOptions, asOfDate: projectionContext?.assessmentDate });
    const expenseAssessment = assessExpenses({ expenses, factFind, strategyOptions, projectionContext });
    const { livingExpenses, capitalExpenses, partnerRent, integratedAgedCareCost } = expenseAssessment;
    // What the household pays to live where it lives — rent, village fees, board (8 Oct 2026).
    const housingCosts = Number(expenseAssessment.housingCosts) || 0;
    const housingCostsLabel = expenseAssessment.housingCostsLabel || 'rent';

    const totalGrossIncome = otherIncomeCash + investmentIncomeCash + abpIncomeCash + agePension + reverseMortgageIncome + heasIncome + rentAssistanceIncome;
    const totalExpenses = livingExpenses + totalRentalExpenses + totalTax + totalLoanRepayments + capitalExpenses + partnerRent + integratedAgedCareCost + housingCosts;
    const cashFlow = totalGrossIncome - totalExpenses;

    /*
     * Investment income, ITEMISED BY KIND.
     *
     * One "Investment Income (Cash)" line used to carry bank interest, share dividends and property
     * rent together. The total was right, but an adviser going through it with a client could not
     * say which asset produced what — and calling a rental property's income "investment income
     * (cash)" reads like a category error to the person who owns the house (reported 2026-08-22).
     *
     * Zero-value kinds are omitted rather than printed as $0, and a client whose investment income
     * happens to be all one kind still sees it named ("Interest") rather than generically. The four
     * parts sum to `investmentIncomeCash`, which is what every total is still struck from — nothing
     * here changes a figure.
     */
    const investmentIncomeLines = [
        { label: 'Interest (bank & term deposits)', value: investmentIncomeParts.interest, series: 'investment' },
        { label: 'Dividends (shares & funds)', value: investmentIncomeParts.dividends, series: 'investment' },
        { label: 'Rent (property)', value: investmentIncomeParts.rent, series: 'investment' },
        { label: 'Trust & Company Income', value: investmentIncomeParts.structures, series: 'investment' },
        { label: 'Other Investment Income', value: investmentIncomeParts.other, series: 'investment' },
    ].filter((line) => line.value !== 0);

    const cashFlowBreakdown: any[] = [
        // Fall back to the single line when the parts are all zero but the total is not, so a future
        // income source added to the walk and forgotten here still appears somewhere.
        ...(investmentIncomeLines.length > 0
            ? investmentIncomeLines
            : [{ label: 'Investment Income (Cash)', value: investmentIncomeCash, series: 'investment' }]),
        /*
         * A NOTE, not a cashflow line: `note: true` marks a row that explains the figures without
         * being one of them, and every consumer that sums this breakdown must skip it.
         *
         * Reinvested dividends are taxed but never banked, so they are correctly absent from cash
         * income — but absent and forgotten look identical on screen. An adviser with $150,000 of
         * shares at 4% and no dividend line anywhere asked where the share income had gone
         * (2026-08-22). Now it says.
         */
        ...(reinvestedDividends > 0
            ? [{ label: 'Dividends reinvested (taxed, not received as cash)', value: reinvestedDividends, note: true }]
            : []),
        ...(abpIncomeCash > 0 ? [{ label: 'ABP Income', value: abpIncomeCash }] : []),
        { label: 'Other Income (Cash)', value: otherIncomeCash },
        { label: 'Age Pension (Est.)', value: agePension },
        ...(reverseMortgageIncome > 0 ? [{ label: 'Reverse Mortgage Payments', value: reverseMortgageIncome }] : []),
        ...(heasIncome > 0 ? [{ label: 'HEAS Income', value: heasIncome }] : []),
        ...(rentAssistanceIncome > 0 ? [{ label: 'Rent Assistance', value: rentAssistanceIncome }] : []),
        { label: 'Less: Rental Expenses', value: -totalRentalExpenses },
        { label: 'Less: Living Expenses', value: -livingExpenses },
        ...(housingCosts > 0 ? [{ label: `Less: Housing Costs (${housingCostsLabel})`, value: -housingCosts }] : []),
        ...(integratedAgedCareCost > 0 ? [{ label: 'Less: Aged Care Costs', value: -integratedAgedCareCost }] : []),
        { label: 'Less: Capital Expenses', value: -capitalExpenses },
        { label: 'Less: Loan Repayments', value: -totalLoanRepayments },
    ];

    if (partnerRent > 0) {
        cashFlowBreakdown.push({ label: "Less: Partner's Rent", value: -partnerRent });
    }

    if (isPartnered) {
        cashFlowBreakdown.push({
            label: incomeTax.client.netTax < 0 ? `Add: ${client.firstName || 'Client'} Tax Refund` : `Less: ${client.firstName || 'Client'} Tax`,
            value: -incomeTax.client.netTax,
        });
        cashFlowBreakdown.push({
            label: incomeTax.partner.netTax < 0 ? `Add: ${partner.firstName || 'Partner'} Tax Refund` : `Less: ${partner.firstName || 'Partner'} Tax`,
            value: -incomeTax.partner.netTax,
        });
    } else {
        cashFlowBreakdown.push({
            label: totalTax < 0 ? 'Add: Tax Refund' : 'Less: Estimated Tax',
            value: -totalTax,
        });
    }

    const radFundingEntityCgtTotal = (radFundingEntityCgtItems || []).reduce((sum: number, item: any) => sum + (item.value || 0), 0);
    if (radFundingEntityCgtTotal !== 0) {
        radFundingEntityCgtItems.forEach((item: any) => cashFlowBreakdown.push(item));
    }

    const cashFlowWithEntityRadCgt = cashFlow + radFundingEntityCgtTotal;

    return {
        netWorth,
        cashFlow: cashFlowWithEntityRadCgt,
        socialSecurity: agePension,
        // Said separately (7 Oct 2026): the screens and the living-arrangement tests need to know
        // whether Rent Assistance is being paid and on which thresholds the pension was struck.
        rentAssistanceIncome,
        isHomeowner,
        investmentIncome: investmentIncomeCash + abpIncomeCash,
        abpIncome: abpIncomeCash,
        // Salary component of otherIncomeCash, surfaced so the projection engine can pay it monthly
        // (see incomePlugin). Additive only — every other returned figure already accounts for salary.
        salaryIncomeCash,
        // Gross investment-property rent and expenses, plus this year's living expenses, surfaced
        // (additively) so the projection engine can pay/charge them monthly too (see expensesPlugin).
        // Already reflected in the annual cashFlow.
        investmentPropertyRentCash,
        investmentPropertyExpenseCash,
        livingExpensesCash: livingExpenses,
        // This period's one-off capital expenses, surfaced so the projection engine can REPORT the
        // figure on its row (the projections modal itemises it and adds it into "Total Expenses").
        // Already inside the annual `cashFlow` above — surfacing it changes no calculation, it ends
        // a silence: a year could drop by a $50,000 capital expense with nothing in the breakdown
        // saying why, because the row carried a hardcoded 0.
        capitalExpensesCash: capitalExpenses,
        // Each personally-serviced liability's repayment schedule, surfaced (additively) so the
        // projection engine can pay it at the frequency the client actually services it — weekly,
        // fortnightly or monthly (see loanRepaymentPlugin). Already reflected in the annual cashFlow
        // via `totalLoanRepayments`; the trust/company pool is not among them (see section 5).
        loanRepaymentSchedules: loanRepayments.schedules,
        netTax: totalTax,
        cashFlowBreakdown,
        taxBreakdown,
        socialSecurityBreakdown,
        assessableAssets,
        assessableAssetsNoHome,
        deemedIncome,
        assessableIncomeFromPensions,
        netRentalIncomeForPension,
        otherAssessableIncome,
        partnerAssessableIncomeFromPensions,
        partnerNetRentalIncomeForPension,
        partnerOtherAssessableIncome,
        // Struck by the means test above (which sums the same components), and read by the aged-care
        // means test as well as the pension one — hence reported whatever the client's age.
        totalAssessableIncome,
        clientAssessableIncome: clientTotalTaxable,
        partnerAssessableIncome: partnerTotalTaxable,
        // Everything the CGT engine needs to tax a capital gain DIFFERENTIALLY under the same rules
        // applied above — this year's (possibly indexed) `rates` bag and each person's position, in the
        // exact shape `incomeTax.ts`'s assessor takes, so the gain is re-taxed by THE tax system rather
        // than an approximation of it. Without this, CGT was estimated as a flat marginal rate on the
        // whole gain, which ignored bracket steps, the Medicare levy and the SAPTO/LITO taper the gain
        // causes. See disposalCgt.ts.
        taxContext: {
            rates,
            relationshipStatus,
            isPartnered,
            illnessSeparated,
            // Age Pension / other income support in this year exempts the client from the 2027 reform's
            // 30% minimum tax on the real gain (see CGT_REGIME.incomeSupportExempt).
            receivesIncomeSupport: (agePension || 0) > 0,
            client: {
                taxableIncome: clientTotalTaxable,
                frankingCredits: clientTaxable.frankingCredits || 0,
                age: clientAge,
                hasPhi: client?.phi === true,
            },
            partner: {
                taxableIncome: partnerTotalTaxable,
                frankingCredits: partnerTaxable.frankingCredits || 0,
                age: partnerAge,
                hasPhi: partner?.phi === true,
            },
        },
    };
};

/** The year's full assessment: assets, income, the Age Pension, tax, cashflow and their breakdowns. */
export const calculateFinancials = (
    factFind: FactFind,
    strategyOptions: StrategyOptions = {},
    yearsSinceRented = 0,
    projectionContext: any = {},
    options: CalculateFinancialsOptions = {},
) => calculateFinancialsCore(factFind, strategyOptions, yearsSinceRented, projectionContext, options)!;

/**
 * THE AGE PENSION ALONE (24 Oct 2026: a cold aged care analysis ran past the three-minute limit). The
 * projection re-assesses the pension on every fortnightly payday — 26 times a year per candidate, several
 * hundred candidates — and read only `socialSecurity` from a full `calculateFinancials` each time, so two
 * thirds of an analysis was spent striking tax, cashflow and their breakdown text only to throw them
 * away. This runs the same assessment, the same code, and stops once the entitlement is struck: the
 * figure is identical by construction (nothing after section 3 feeds it).
 */
export const calculateAgePensionEntitlement = (
    factFind: FactFind,
    strategyOptions: StrategyOptions = {},
    yearsSinceRented = 0,
    projectionContext: any = {},
    options: CalculateFinancialsOptions = {},
): number => {
    const probe: AgePensionProbe = { agePension: 0 };
    calculateFinancialsCore(factFind, strategyOptions, yearsSinceRented, projectionContext, options, probe);
    return probe.agePension;
};

// Note: computeMeansTestedFees, getMtaBreakdown and calculateAgedCareCosts (and their private
// per-day-tested-amount helpers) live in server/src/rest/calculation/agedCareCostsEngine.ts —
// they're server-only (non-reactive, no shared caller), unlike calculateFinancials below which
// entityDisposalTax.ts needs at runtime for its reactive CGT UI.

export { calculateAge, formatCurrency };


// The tax rules moved to incomeTax.ts with the rest of section 6, and are re-exported here because
// existing callers import them from this module. `calculateMedicareLevy` is newly exported: it was a
// closure inside `calculateFinancials`, unreachable and untestable, only because there was nowhere to
// put the per-call indexed `rates` it depends on — incomeTax.ts takes those as an argument.
export { calculateTax, calculateLitoEntitlement, calculateSaptoEntitlement, calculateMedicareLevy } from '../../../../../shared/src/calculation/incomeTax.ts';

// The capital-expense helpers moved to expenses.ts with the rest of section 7's expense assessment, and
// the anchor-date helpers to projectionAnchor.ts (which expenses.ts needs, and which keeping here would
// have made the two modules import each other's values at runtime). Both are re-exported because
// existing callers — the server's projection engine, fyDualWorldBlend, retirementVillageProjections and
// the shared tests — import them from this module.
export { parseCapitalExpenseMonthDate, sumCapitalExpensesForStrategyYear, sumCapitalExpensesForProjectionPeriod } from './expenses.ts';
export { getDefaultProjectionAnchorDate, resolveProjectionAnchorDate } from './projectionAnchor.ts';
